import './AppSideMenu.scss'
import {Link, useLocation} from "react-router-dom";
import {getMenuButtons} from "../Main/MenuButtons/MenuButtonsLib";
import {get, map} from "lodash";
import {useContext, useState, useEffect} from "react";
import {UserReactContext} from "../../contexts/UserContext";
import personIcon from "../../assets/images/personIcon.png";
import {useMediaQuery} from 'react-responsive';
import {CompanyReactContext} from "../../contexts/CompanyContext";
import {I18nReactContext} from "../../contexts/I18nContext";
import {UserIconMenu} from "../UserIconMenu/UserIconMenu";
import {UserLanguageMenu} from "../UserLanguageMenu/UserLanguageMenu";
import {FeatureFlags} from "../../Utils/Constants";
import {useClearFiltersOnRouteChange} from "../../Hooks/useFilterPersistence";

/**
 * The main side menu of the app
 * @param {object} params - The component props
 * @param {string} [params.path] - The current path
 * @param {Array<object>} params.adminOrganizations - The organizations the user manages
 * @returns {JSX.Element} The side menu
 */
export const AppSideMenu = ({path, adminOrganizations}) => {
    const {i18n} = useContext(I18nReactContext);
    const isMobile = useMediaQuery({query: `(max-width: 1024px)`});
    const [isMenuOpen, setIsMenuOpen] = useState(!isMobile)
    const location = useLocation()
    const userContext = useContext(UserReactContext)
    const companyContext = useContext(CompanyReactContext)
    const featureFlags = companyContext.featureFlags

    // Clear Learning Management filters when navigating away from that section
    useClearFiltersOnRouteChange();

    // Check if user has approver access on mount (only for non-admin users with relevant feature flags)
    useEffect(() => {
        let isManagingCompany = userContext.isManagingCompany()
        let isSuperAdmin = userContext.isUserSuperAdmin()
        let hasUploadDocumentFF = get(featureFlags, `${FeatureFlags.UPLOAD_DOCUMENT}`, false);
        let hasFrontalCourseFF = get(featureFlags, `${FeatureFlags.FRONTAL_COURSE}`, false);
        let hasOnlineCourseFF = get(featureFlags, `${FeatureFlags.ONLINE_COURSE}`, false);
        let hasUserPendingApprovals = userContext.isUserApprover();
        const shouldCheckApprovals = !isManagingCompany &&
                                     !isSuperAdmin &&
                                     (hasUploadDocumentFF || hasFrontalCourseFF || hasOnlineCourseFF);

        if (shouldCheckApprovals && !hasUserPendingApprovals) {
            userContext.checkUserApprovals();
        }
    }, [featureFlags]);

    const buttons = getMenuButtons({
        adminOrgsLength: get(adminOrganizations, 'length', 0),
        isCompanyAdmin: userContext.isManagingCompany(),
        isSuperAdmin: userContext.isUserSuperAdmin(),
        hasUserPendingApprovals: userContext.isUserApprover(),
        isCourseApprover: userContext.isCourseApprover(),
        isDocumentApprover: userContext.isDocumentApprover(),
        hasExpiringPartnerSecret: userContext.hasExpiringPartnerSecret,
        featureFlags
    })
    const isSuperAdmin = userContext.isUserSuperAdmin()

    useEffect(() => {
        if (isSuperAdmin) {
            userContext.checkExpiringPartnerSecrets();
        }
    }, [isSuperAdmin]);

    return (
        <div className={`AppSideMenu ${isMenuOpen ? 'open' : 'closed'} ${isSuperAdmin ? 'super-admin' : ''} `}>
            {isMobile ? <div className="hamburgerMenu">
                <div onClick={() => setIsMenuOpen(!isMenuOpen)}>{isMenuOpen ? 'X' : '☰'}</div>
            </div> : ''}
            <div className="AppSideMenuHeader">
                <div className="AppSideMenuHeaderLogoWrapper">
                    <div className="AppSideMenuHeaderLogo">
                        <img src={personIcon} alt=""/>
                    </div>
                </div>
                <div className="AppSideMenuHeaderTitle">
                    <div>{i18n.translate('AppSideMenu.welcome')}</div>
                    <div className="UserNameAndMenu">{userContext.getUserName()}<UserIconMenu size={isMobile ? 'small' : ''}/></div>
                </div>
            </div>
            <div className="AppSideMenuButtons">
                {map(buttons, (button, index) => {
                    let selected = false
                    map(button.detectSelected, (selectedString) => {
                        if (location.pathname.includes(selectedString)) {
                            selected = true
                        }
                    })
                    return <Link className={`AppSideMenuButton ${selected ? 'selected' : ''} ${button.isAlert ? 'AppSideMenuButton--alert' : ''}`} key={button.nameKey}
                                 to={button.link}>
                        <div className="buttonText">{i18n.translate(button.nameKey)}</div>
                        <div className="buttonIcon">
                            {button.icon()}
                        </div>
                    </Link>
                })}

            </div>
            <div className="AppSideMenuFooter">
                {get(featureFlags,`${[FeatureFlags.LANGUAGES]}`) ? <UserLanguageMenu/> : ''}
                {/*<div className="AppSideMenuFooterButton">*/}
                {/*    <Link to="/logout">*/}
                {/*        <div className="buttonText">{i18n.translate('menuItems.logout')}</div>*/}
                {/*        <div className="buttonIcon">*/}
                {/*            <LogoutOutlinedIcon/>*/}
                {/*        </div>*/}
                {/*    </Link>*/}
                {/*</div>*/}
                {/*<div className="AppSideMenuFooterButton">*/}
                {/*    <Link to="/changePassword">*/}
                {/*        <div className="buttonText">{i18n.translate('menuItems.changePassword')}</div>*/}
                {/*        <div className="buttonIcon">*/}
                {/*            <SyncLockOutlinedIcon/>*/}
                {/*        </div>*/}
                {/*    </Link>*/}
                {/*</div>*/}
                
                {/* Legal Links */}
                <div className="AppSideMenuLegalLinksWrapper">
                    <div className="AppSideMenuLegalLinks">
                        <a 
                            href="https://weezbee.ai/%d7%aa%d7%a7%d7%a0%d7%95%d7%9f-%d7%94%d7%a9%d7%99%d7%9e%d7%95%d7%a9/" 
                            target="_blank" 
                            className="legalLink"
                        >
                            {i18n.translate('AppSideMenu.legalLinks.termsOfUse')}
                        </a>
                        <a
                            href="https://weezbee.ai/%d7%aa%d7%a7%d7%a0%d7%95%d7%9f-%d7%94%d7%a9%d7%99%d7%9e%d7%95%d7%a9-%d7%a4%d7%a8%d7%98%d7%99%d7%95%d7%aa/"
                            target="_blank"
                            className="legalLink"
                        >
                            {i18n.translate('AppSideMenu.legalLinks.privacyPolicy')}
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}